import { useMemo, useState, type ReactNode } from "react";
import {
    ArrowRightLeft,
    BuildingIcon,
    ChevronDown,
    ChevronRight,
    FileTextIcon,
    FlagIcon,
    MailIcon,
    MessageCircleIcon,
    PhoneIcon,
    Settings,
    TimerIcon,
    UserCheck,
    UserIcon,
    WorkflowIcon,
} from "lucide-react";
import { Badge } from "@component/badge";
import { CandidateActivityGroup, CandidateActivityHistory } from "@type/candidate";
import { mapApplicantStage } from "@constant/app";
import { convertCapitalizeWords, convertMarkdownToHtml, renameAssessmentToScreening } from "@util/string";
import { compareIsoDatesDesc, formatShortDate, formatTimeAgo, parseFormat } from "@util/date";

/**
 * #3254 (BE #3995): distinct icon per stable `action_code` FAMILY. Never key
 * off the free-text `action_header` — it gets reworded in bulk (#3737, #3976).
 * Unknown/absent codes return null so callers fall back to today's icon —
 * never a blank slot. No information is removed; only the glyph changes.
 */
function actionCodeIcon(code?: string | null): ReactNode | null {
    if (!code) return null;
    const c = code.toLowerCase();
    const cls = "h-5 w-5";
    if (c.startsWith("call_") || c.includes("phone")) return <PhoneIcon className={`${cls} text-sky-600 dark:text-sky-400`} />;
    if (c.startsWith("document_") || c.startsWith("doc_") || c.startsWith("cv_")) return <FileTextIcon className={`${cls} text-indigo-600 dark:text-indigo-400`} />;
    if (c.startsWith("gate_") || c.includes("knockout") || c.includes("flag")) return <FlagIcon className={`${cls} text-amber-600 dark:text-amber-400`} />;
    if (c.startsWith("deadline_")) return <TimerIcon className={`${cls} text-amber-600 dark:text-amber-400`} />;
    if (c.includes("email") || c.includes("invit") || c.includes("reminder")) return <MailIcon className={`${cls} text-blue-500 dark:text-blue-400`} />;
    if (c.startsWith("flow_") || c.includes("step") || c.includes("intake")) return <WorkflowIcon className={`${cls} text-blue-600 dark:text-blue-400`} />;
    if (c.includes("status")) return <ArrowRightLeft className={`${cls} text-gray-600 dark:text-gray-400`} />;
    if (c.includes("lead") || c.includes("whatsapp") || c.includes("message") || c.includes("chat")) return <MessageCircleIcon className={`${cls} text-green-600 dark:text-green-500`} />;
    return null;
}

interface ActivityLogTabContentProps {
    activityGroups: CandidateActivityGroup[];
}

export default function ActivityLogTabContent({
    activityGroups,
}: ActivityLogTabContentProps) {
    const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
    const [collapsedActivities, setCollapsedActivities] = useState<Record<string, boolean>>({});

    // Re-group entries by the viewer's LOCAL day of action_at (#3118). The BE
    // buckets by UTC day, so an early-morning-local activity can fall into the
    // previous day; re-bucketing locally keeps the group header ("Today" /
    // "Yesterday") in sync with the per-item timestamps.
    const localDayKey = (iso: string): string => {
        const d = new Date(iso);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
            d.getDate()
        ).padStart(2, "0")}`;
    };
    // "Interview call failed" entries are noise for HR (retries are automatic
    // and the outcome shows up as its own entry) — hidden per product request.
    // Markdown markers are stripped before matching so a bolded title still hides.
    const isHiddenActivity = (entry: CandidateActivityHistory): boolean =>
        (entry.action_title || "")
            .replace(/[*_`]/g, "")
            .trim()
            .toLowerCase()
            .startsWith("interview call failed");
    const localGroups = useMemo(() => {
        const byDay = new Map<string, CandidateActivityHistory[]>();
        for (const group of activityGroups) {
            for (const entry of group.entries || []) {
                if (isHiddenActivity(entry)) continue;
                const key = localDayKey(entry.action_at);
                const bucket = byDay.get(key);
                if (bucket) bucket.push(entry);
                else byDay.set(key, [entry]);
            }
        }
        return Array.from(byDay.entries())
            .filter(([, entries]) => entries.length > 0)
            .map(([date, entries]) => ({
                date,
                entries: entries
                    .slice()
                    .sort((a, b) => compareIsoDatesDesc(a.action_at, b.action_at)),
            }))
            .sort((a, b) => b.date.localeCompare(a.date));
    }, [activityGroups]);

    const toggleGroup = (date: string) => {
        setCollapsedGroups((prev) => ({
            ...prev,
            [date]: !prev[date],
        }));
    };

    const toggleActivity = (activityId: string) => {
        setCollapsedActivities((prev) => ({
            ...prev,
            [activityId]: !prev[activityId],
        }));
    };

    // Candidate notifications come from the BE as action_type "email", but they
    // aren't email-only (some also go via WhatsApp), so HR sees them labelled
    // "Notification". Other action types keep their capitalized label.
    const activityTypeLabel = (actionType: string) =>
        actionType === "email" ? "Notification" : convertCapitalizeWords(actionType);

    // Channel styling for a content note ("Email Content" / "WhatsApp Content"):
    // email → blue icon + label + left border, whatsapp → green. Matched on the
    // label text since the channel isn't a separate field. Other labels keep the
    // original gray label + green border.
    const contentLabelStyle = (label?: string) => {
        const text = (label || "").toLowerCase();
        if (text.includes("whatsapp")) {
            return {
                icon: <MessageCircleIcon className="h-3 w-3 text-green-600 dark:text-green-500" />,
                labelClass: "text-green-600 dark:text-green-500",
                borderClass: "border-green-300 dark:border-green-700",
            };
        }
        if (text.includes("email")) {
            return {
                icon: <MailIcon className="h-3 w-3 text-blue-500 dark:text-blue-400" />,
                labelClass: "text-blue-600 dark:text-blue-400",
                borderClass: "border-blue-300 dark:border-blue-700",
            };
        }
        return {
            icon: null,
            labelClass: "",
            borderClass: "border-green-300 dark:border-green-700",
        };
    };

    return (
        <div>
            <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-6">
                ACTIVITY LOG
            </h4>
            <div className="space-y-8">
                {localGroups.length > 0 ? (
                    <>
                        {localGroups.map((item, index) => (
                            <div key={index} className="space-y-4">
                                <button
                                    onClick={() => toggleGroup(item.date)}
                                    className="flex items-center gap-2 w-full text-left group hover:bg-gray-50 dark:hover:bg-gray-800/50 rounded-md px-2 py-1 -mx-2 transition-colors"
                                >
                                    {collapsedGroups[item.date] ? (
                                        <ChevronRight className="h-4 w-4 text-gray-400" />
                                    ) : (
                                        <ChevronDown className="h-4 w-4 text-gray-400" />
                                    )}
                                    <h5 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                                        {formatShortDate(item.entries[0]?.action_at ?? item.date)}
                                    </h5>
                                    <span className="text-xs text-gray-400 dark:text-gray-500">
                                        ({item.entries.length}{" "}
                                        {item.entries.length === 1 ? "activity" : "activities"})
                                    </span>
                                </button>
                                {!collapsedGroups[item.date] && (
                                    <div className="space-y-5 pl-6">
                                        {(item.entries || []).slice().sort((a, b) => compareIsoDatesDesc(a.action_at, b.action_at)).map((activity, index) => (
                                            <div key={index} className="space-y-3">
                                                {!activity.action_actor && !activity.action_header ? (
                                                    <>
                                                        <div className="flex gap-3 group items-start">
                                                            <div className="flex-shrink-0">
                                                                <div
                                                                    className="w-9 rounded-md bg-gray-100 dark:bg-gray-800 flex items-center justify-center group-hover:bg-gray-200 dark:group-hover:bg-gray-700 transition-colors py-2.5"
                                                                    style={{
                                                                        paddingRight: "10px",
                                                                        paddingLeft: "10px",
                                                                    }}
                                                                >
                                                                    {actionCodeIcon(activity.action_code) ?? (activity.action_type === "email" ? (
                                                                        <MailIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                                                                    ) : activity.action_type === "status_change" ? (
                                                                        <ArrowRightLeft className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                                                                    ) : activity.action_type === "manual" ? (
                                                                        <UserCheck className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                                                                    ) : activity.action_type === "system" ? (
                                                                        <Settings className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                                                                    ) : null)}
                                                                </div>
                                                            </div>
                                                            <div className="flex-1 min-w-0 flex items-start justify-between gap-3">
                                                                <div className="flex-1 min-w-0">
                                                                    <p className="text-sm text-gray-900 dark:text-gray-100 mb-1"
                                                                        dangerouslySetInnerHTML={{ __html: convertMarkdownToHtml(renameAssessmentToScreening(activity.action_title) || "Unknown action") }}
                                                                    />
                                                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                                                        {formatTimeAgo(activity.action_at)} • {parseFormat(activity.action_at, "h:mm A")}
                                                                    </p>
                                                                </div>
                                                                <div className="flex-shrink-0">
                                                                    <Badge
                                                                        variant="secondary"
                                                                        className={activity.action_type === "email" ? "text-xs bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300 flex-shrink-0" : activity.action_type === "status_change" ? "text-xs bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300 flex-shrink-0" : activity.action_type === "manual" ? "text-xs bg-purple-50 text-purple-700 dark:bg-purple-900/20 dark:text-purple-300 flex-shrink-0" : activity.action_type === "system" ? "text-xs bg-gray-50 text-gray-700 dark:bg-gray-800 dark:text-gray-300 flex-shrink-0" : ""}
                                                                        style={{
                                                                            paddingTop: "4px",
                                                                            paddingBottom: "4px",
                                                                            paddingLeft: "8px",
                                                                            paddingRight: "8px",
                                                                            border: "0.909091px solid rgba(0, 0, 0, 0)",
                                                                            borderRadius: "1rem",
                                                                            boxShadow: "none",
                                                                        }}
                                                                    >
                                                                        {activityTypeLabel(activity.action_type)}
                                                                    </Badge>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        {activity.action_content && (
                                                            <div className="flex gap-3">
                                                                <div className="w-9 flex-shrink-0"></div>
                                                                <div className="flex-1">
                                                                    <button
                                                                        onClick={() => toggleActivity(index + "-note")}
                                                                        className="flex items-center gap-2 mb-2 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                                                                    >
                                                                        {collapsedActivities[index + "-note"] ? (
                                                                            <ChevronRight className="h-3 w-3" />
                                                                        ) : (
                                                                            <ChevronDown className="h-3 w-3" />
                                                                        )}
                                                                        <span className={`font-medium inline-flex items-center gap-1 ${contentLabelStyle(activity.action_label).labelClass}`}>
                                                                            {contentLabelStyle(activity.action_label).icon}
                                                                            {activity.action_label || "Note"}
                                                                        </span>
                                                                    </button>
                                                                    {!collapsedActivities[index + "-note"] && (
                                                                        <div
                                                                            className={`text-xs text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/50 rounded-md p-3 border-l-2 ${contentLabelStyle(activity.action_label).borderClass}`}
                                                                        >
                                                                            {(activity.action_content && typeof activity.action_content === "string") && (
                                                                                <p className="whitespace-pre-line" dangerouslySetInnerHTML={{ __html: convertMarkdownToHtml(activity.action_content || "") }} />
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </>
                                                ) : (
                                                    <>
                                                        <div className="flex items-start justify-between gap-4 group">
                                                            {/* Icon based on source */}
                                                            <div className="flex-shrink-0">
                                                                <div className="w-[52px] h-[52px] rounded-md bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                                                                    {actionCodeIcon(activity.action_code) ?? (activity.action_actor !== "Candidate" ? (
                                                                        <BuildingIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                                                                    ) : (
                                                                        <UserIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                                                                    ))}
                                                                </div>
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">
                                                                    {renameAssessmentToScreening(activity.action_header) || "Unknown Action"}
                                                                </p>
                                                                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2"
                                                                    dangerouslySetInnerHTML={{ __html: convertMarkdownToHtml(renameAssessmentToScreening(activity.action_description)) }}
                                                                />
                                                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                                                    {parseFormat(activity.action_at, "D MMM YYYY")} • {parseFormat(activity.action_at, "h:mm A")} {(activity.action_actor) ? `• ${activity.action_actor}` : activity.action_actor}
                                                                </p>
                                                            </div>
                                                            <div className="flex-shrink-0 text-right">
                                                                {activity.action_stage && (
                                                                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                                                        Stage: <span className="font-semibold">{activity.action_stage.map((stage) => mapApplicantStage[stage] || renameAssessmentToScreening(stage)).join(" → ")}</span>
                                                                    </p>
                                                                )}
                                                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                                                    {activity.action_actor === "Candidate" ? activity.action_actor : activityTypeLabel(activity.action_type)}
                                                                </p>
                                                            </div>
                                                        </div>
                                                        {(activity.action_content && typeof activity.action_content !== "string" && activity.action_content.length > 0) && activity.action_content.map((contentItem, contentIndex) => (
                                                            <div className="flex gap-3" key={contentIndex}>
                                                                <div className="w-9 flex-shrink-0"></div>
                                                                <div className="flex-1">
                                                                    <button
                                                                        onClick={() => toggleActivity(index + "-" + contentIndex + "-note")}
                                                                        className="flex items-center gap-2 mb-2 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                                                                    >
                                                                        {collapsedActivities[index + "-" + contentIndex + "-note"] ? (
                                                                            <ChevronRight className="h-3 w-3" />
                                                                        ) : (
                                                                            <ChevronDown className="h-3 w-3" />
                                                                        )}
                                                                        <span className={`font-medium inline-flex items-center gap-1 ${contentLabelStyle(contentItem.label).labelClass}`}>
                                                                            {contentLabelStyle(contentItem.label).icon}
                                                                            {contentItem.label || "Note"}
                                                                        </span>
                                                                    </button>
                                                                    {!collapsedActivities[index + "-" + contentIndex + "-note"] && (
                                                                        <div
                                                                            className={`text-xs text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/50 rounded-md p-3 border-l-2 ${contentLabelStyle(contentItem.label).borderClass}`}
                                                                        >
                                                                            <p className="whitespace-pre-line" dangerouslySetInnerHTML={{ __html: convertMarkdownToHtml(contentItem.content || "") }} />
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </>
                ) : (
                    <p className="text-sm text-gray-600 dark:text-gray-400">No activity history available.</p>
                )}
            </div>
        </div>
    );
}
